<template>
    <div class="event-service-import">
        <h1 class="app-h1">
            Импорт услуг и расписания
        </h1>

        <div class="event-service-import__panel">
            <el-alert
                type="info"
                :closable="false"
                class="event-service-import__alert"
                title="Загрузите xlsx-файл с листами services и schedule"
            />

            <div class="event-service-import__field">
                <div class="event-service-import__label">
                    Файл импорта
                </div>
                <input
                    ref="fileInput"
                    type="file"
                    accept=".xlsx,.xls"
                    @change="_onFileChange"
                >
            </div>

            <div
                v-if="fileName"
                class="event-service-import__filename"
            >
                {{ fileName }}
            </div>

            <div class="event-service-import__actions">
                <el-button
                    :disabled="isLoading"
                    @click="_resetFile"
                >
                    Очистить
                </el-button>
                <el-button
                    type="primary"
                    :loading="isLoading"
                    :disabled="!file"
                    @click="_submit"
                >
                    Импортировать
                </el-button>
                <el-button
                    type="danger"
                    :loading="isClearLoading"
                    @click="_clearServices"
                >
                    Удалить все услуги
                </el-button>
            </div>

            <div
                v-if="isLoading || statusMessage"
                class="event-service-import__status"
            >
                <el-progress
                    v-if="isLoading"
                    :percentage="100"
                    :indeterminate="true"
                    :show-text="false"
                    status="success"
                />

                <div
                    v-if="isLoading"
                    class="event-service-import__status-text"
                >
                    Импорт выполняется...
                </div>

                <el-alert
                    v-if="statusMessage"
                    :type="statusType"
                    :closable="false"
                    class="event-service-import__status-alert"
                    :title="statusMessage"
                />

                <el-alert
                    v-if="statusErrors.length"
                    type="error"
                    :closable="false"
                    class="event-service-import__status-alert"
                    title="Ошибки импорта"
                >
                    <ol class="event-service-import__status-errors">
                        <li
                            v-for="(error, index) in statusErrors"
                            :key="index"
                            class="event-service-import__status-error"
                        >
                            {{ error }}
                        </li>
                    </ol>
                </el-alert>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { Event } from '@/api';

export default {
    name: 'EventServiceImport',

    props: {
        eventId: {
            type: String,
            default: null
        }
    },

    data() {
        return {
            isLoading: false,
            isClearLoading: false,
            file: null,
            statusMessage: '',
            statusType: 'info',
            statusErrors: []
        };
    },

    computed: {
        fileName() {
            return _.get(this.file, 'name', '');
        }
    },

    methods: {
        _onFileChange(event) {
            const files = _.get(event, 'target.files', []);
            this.file = files && files.length ? files[0] : null;
            this.statusMessage = '';
            this.statusErrors = [];
        },

        _resetFile() {
            this.file = null;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = null;
            }
        },

        _submit() {
            if (!this.file) {
                return;
            }

            this.isLoading = true;
            this.statusMessage = '';
            this.statusErrors = [];
            Event.Service.importFullSchedule({
                eventId: this.eventId,
                file: this.file
            })
                .then(result => {
                    const errors = this._extractErrors(result);
                    const servicesWithSchedule = _.get(result, 'services_with_schedule', 0);
                    const servicesWithoutSchedule = _.get(result, 'services_without_schedule', 0);
                    const summary = `Добавлено услуг с расписанием: ${servicesWithSchedule}, без расписания: ${servicesWithoutSchedule}`;

                    this.statusErrors = errors;

                    if (errors.length) {
                        this.statusType = servicesWithSchedule > 0 ? 'warning' : 'error';
                        this.statusMessage = servicesWithSchedule > 0
                            ? `Импорт выполнен частично. ${summary}`
                            : `Импорт завершился с ошибками. ${summary}`;
                        this.$message.warning(this.statusMessage);
                    } else {
                        this.statusType = 'success';
                        this.statusMessage = `Импорт завершен. ${summary}`;
                        this.$message.success(this.statusMessage);
                    }

                    this._resetFile();
                })
                .catch(error => {
                    const errors = this._extractErrors(error);
                    const message = errors.length
                        ? 'Импорт завершился с ошибками'
                        : (_.get(error, 'message') || 'При импорте произошла ошибка');
                    this.statusType = 'error';
                    this.statusMessage = message;
                    this.statusErrors = errors;
                    this.$message.error(message);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        _clearServices() {
            this.$confirm('Удалить все услуги без записанных участников?', 'Подтверждение', {
                confirmButtonText: 'Удалить',
                cancelButtonText: 'Отмена',
                type: 'warning'
            }).then(() => {
                this.isClearLoading = true;
                Event.Service.clearEventServices({ eventId: this.eventId })
                    .then(result => {
                        const deletedCount = result.deleted.length;
                        const skippedCount = result.skipped.length;
                        let message = `Удалено услуг: ${deletedCount}`;
                        if (skippedCount) {
                            message += `. Пропущено (есть участники): ${skippedCount}`;
                        }
                        this.$message.success(message);
                    })
                    .catch(error => {
                        const message = _.get(error, 'message') || 'Ошибка при удалении услуг';
                        this.$message.error(message);
                    })
                    .finally(() => {
                        this.isClearLoading = false;
                    });
            }).catch(() => {});
        },

        _extractErrors(error) {
            const rawErrors = _.get(error, 'errors');
            if (Array.isArray(rawErrors)) {
                return rawErrors;
            }
            if (_.isPlainObject(rawErrors)) {
                return Object.values(rawErrors).flat().filter(Boolean);
            }

            const message = _.get(error, 'message', '');
            if (_.isString(message) && message.includes('\n')) {
                return message
                    .split('\n')
                    .map(item => item.trim())
                    .filter(item => item && item !== 'Импорт расписания прерван.');
            }

            return [];
        }
    }
};
</script>

<style lang="scss">
@import "@vars";

.event-service-import {
    &__alert {
        margin-bottom: 20px;
    }

    &__panel {
        max-width: 640px;
        padding: 24px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        background: #fff;
    }

    &__field {
        margin-bottom: 16px;
    }

    &__label {
        margin-bottom: 8px;
        font-weight: 700;
    }

    &__filename {
        margin-bottom: 16px;
        color: #606266;
    }

    &__actions {
        display: flex;
        gap: 12px;
    }

    &__status {
        margin-top: 20px;
    }

    &__status-text {
        margin-top: 12px;
        color: #606266;
        font-size: 14px;
    }

    &__status-alert {
        margin-top: 12px;
    }

    &__status-errors {
        margin: 0;
        padding-left: 20px;
    }

    &__status-error {
        margin-top: 6px;
        white-space: pre-line;
    }
}
</style>
